import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { SplitText } from "gsap/SplitText"
import { CustomEase } from "gsap/CustomEase"

gsap.registerPlugin(ScrollTrigger, SplitText, CustomEase)

export class TextAnimations {
  constructor(selector = "[data-split-inview]") {
    this.isInitialized = false
    this.scrollTriggers = []
    this.splitInstances = new WeakMap()
    this.selector = selector

    CustomEase.create("smooth", "0.215, 0.61, 0.355, 1")
  }

  async init() {
    if (this.isInitialized) return
    await document.fonts.ready

    this.initSplitInview()

    this.isInitialized = true
  }

  destroy() {
    this.scrollTriggers.forEach(t => t.kill())
    this.scrollTriggers = []

    for (const [el, instance] of this.splitInstances.entries()) {
      instance.revert()
      this.splitInstances.delete(el)
    }

    this.isInitialized = false
  }

  refresh() {
    ScrollTrigger.refresh()
  }

  initSplitInview() {
    const sections = document.querySelectorAll(this.selector);
    if (!sections.length) return;

    const elements = [];

    sections.forEach(section => {
      if (section.dataset.splitInviewInitialized === "true") return;

      const children = Array.from(section.children);

      children.forEach(child => {
        if (child.classList.contains("u-sr-only")) return;
        if (child.tagName.toLowerCase() === "ul") return;

        if (child.tagName.toLowerCase() === "p") {
          this.addAccessibleFallback(child);
        }

        elements.push(child);
      });

      section.dataset.splitInviewInitialized = "true";
    });

    if (!elements.length) return;

    const rectCache = new Map();

    elements.forEach(el => {
      rectCache.set(el, el.getBoundingClientRect());
    });

    Promise.resolve().then(() => {
      elements.forEach(el => {
        const tag = el.tagName.toLowerCase();
        const params = this.getAnimationParams(tag);

        if (this.splitInstances.has(el)) {
          this.splitInstances.get(el).revert();
        }

        const split = SplitText.create(el, {
          type: "lines",
          mask: "lines",
          tag: "span",
          linesClass: "split-line",
          aria: "hidden",
        });

        this.splitInstances.set(el, split);

        this.setupViewportAnimations(el, split, params);
      });
    });
  }

  getAnimationParams(tag) {
    if (tag.startsWith("h")) {
      return { y: -120, duration: 0.6, stagger: 0.072, ease: "smooth" }
    }
    if (tag === "p") {
      return { y: 120, duration: 0.6, stagger: 0.072, ease: "smooth" }
    }
    return { y: 100, duration: 0.55, stagger: 0.06, ease: "smooth" }
  }

  setupViewportAnimations(child, split, params) {
    const mm = gsap.matchMedia()

    mm.add("(min-width: 992px)", () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: child,
          toggleClass: "is-inview",
          start: "top 85%",
          once: true
        }
      })

      tl.fromTo(
        split.lines,
        { y: params.y },
        {
          y: 0,
          duration: params.duration,
          stagger: params.stagger,
          ease: params.ease
        }
      )

      this.scrollTriggers.push(tl.scrollTrigger)

      return () => {
        gsap.set(split.lines, { clearProps: "all" })
      }
    })

    mm.add("(max-width: 991px)", () => {
      gsap.set(split.lines, { y: 0 })
      return () => gsap.set(split.lines, { clearProps: "all" })
    })
  }

  addAccessibleFallback(child) {
    const originalText = child.textContent.trim()
    if (!originalText) return

    const sr = document.createElement("span")
    sr.className = "u-sr-only"
    sr.textContent = originalText

    child.parentNode.insertBefore(sr, child)

    child.setAttribute("aria-hidden", "true")
  }

}
